// Pure calendar helpers for the custom DateField. Dates are handled as
// `YYYY-MM-DD` strings / {year, month, day} parts (month is 0-based) so the
// component never depends on a Date's timezone for its value — only on local
// year/month/day integers for laying out the grid. Kept React-free so the month
// math is unit-testable (the app has no DOM test environment).

export type Ymd = { year: number; month: number; day: number }; // month: 0-11

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const SHORT_MONTHS = MONTH_NAMES.map((m) => m.slice(0, 3));

/** Parse a strict `YYYY-MM-DD` string; null if malformed or out of range. */
export function parseYmd(value: string): Ymd | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (month < 0 || month > 11) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

/** Serialise to `YYYY-MM-DD`. */
export function formatYmd(d: Ymd): string {
  const mm = String(d.month + 1).padStart(2, '0');
  const dd = String(d.day).padStart(2, '0');
  return `${d.year}-${mm}-${dd}`;
}

/** Friendly label for the trigger, e.g. "17 Jun 2026". */
export function formatHuman(d: Ymd): string {
  return `${d.day} ${SHORT_MONTHS[d.month]} ${d.year}`;
}

export function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this month.
  return new Date(year, month + 1, 0).getDate();
}

/** Weekday (0=Sun … 6=Sat) of the first of the month. */
export function firstWeekday(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

/** Shift a year/month by `delta` months, normalising the wrap across years. */
export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

export function monthLabel(year: number, month: number): string {
  return `${MONTH_NAMES[month]} ${year}`;
}

/**
 * A week-by-week grid (each week 7 cells) for the month. Leading/trailing
 * blanks are `null` so the first day lands under its weekday column and the
 * grid is always a whole number of weeks.
 */
export function monthGrid(year: number, month: number): (number | null)[][] {
  const lead = firstWeekday(year, month);
  const total = daysInMonth(year, month);
  const cells: (number | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

const YEAR_PAST = 5;
const YEAR_FUTURE = 15;

/** The 12 months as selectable options (value is 0-based). */
export function monthOptions(): { value: number; label: string }[] {
  return MONTH_NAMES.map((label, value) => ({ value, label }));
}

/**
 * Years for the picker's year dropdown: a contiguous span from
 * currentYear-5 to currentYear+15, plus `selectedYear` if it falls outside
 * that span (so editing an out-of-range saved date never hides its year).
 * De-duplicated and sorted ascending.
 */
export function yearRange(currentYear: number, selectedYear: number | null): number[] {
  const years = new Set<number>();
  for (let y = currentYear - YEAR_PAST; y <= currentYear + YEAR_FUTURE; y++) {
    years.add(y);
  }
  if (selectedYear !== null) years.add(selectedYear);
  return [...years].sort((a, b) => a - b);
}
